import { cn } from '../../lib/utils';
import { Mouse, Keyboard } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

function getActivityColor(percent) {
  if (percent >= 70) return 'bg-emerald-500';
  if (percent >= 50) return 'bg-amber-500';
  if (percent >= 30) return 'bg-orange-500';
  return 'bg-red-500';
}

function getActivityLabel(percent) {
  if (percent >= 70) return 'High';
  if (percent >= 50) return 'Medium';
  if (percent >= 30) return 'Low';
  return 'Very Low';
}

export function ActivityBar({ overall, mouse, keyboard, size = 'md', showBreakdown = true, className }) {
  const barHeight = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className={cn('space-y-1', className)} data-testid="activity-bar">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <div className={cn('w-full rounded-full bg-slate-200 dark:bg-slate-700', barHeight)}>
            <div
              className={cn('rounded-full transition-all duration-500', barHeight, getActivityColor(overall))}
              style={{ width: `${Math.min(overall, 100)}%` }}
            />
          </div>
        </div>
        <span className={cn('font-semibold tabular-nums min-w-[3rem] text-right', textSize)} data-testid="activity-percent">
          {overall}%
        </span>
      </div>

      {showBreakdown && (mouse > 0 || keyboard > 0) && (
        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-default">
                  <Mouse className="w-3 h-3" /> {mouse}%
                </span>
              </TooltipTrigger>
              <TooltipContent>Mouse activity</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-default">
                  <Keyboard className="w-3 h-3" /> {keyboard}%
                </span>
              </TooltipTrigger>
              <TooltipContent>Keyboard activity</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}

export function ActivityBadge({ percent, className }) {
  const color = percent >= 70
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    : percent >= 50
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    : percent >= 30
    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';

  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', color, className)} data-testid="activity-badge">
      {percent}% — {getActivityLabel(percent)}
    </span>
  );
}

export function formatTrackedTime(seconds) {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
